import React from 'react';

export function WaterTypes() {
  return (
    <div className="space-y-6 text-white/90" style={{ userSelect: "none" }}>
      <div className="bg-gray-600/60p-6">
        <h3 className="text-xl text-white mb-4">Understanding Water Types</h3>
        <p className="mb-4">
          The type of water you use can significantly impact your bread's fermentation, gluten development, and final flavor. Understanding different water types and their effects will help you make better choices for your baking.
        </p>
      </div>
      
      <div className="bg-gray-600/60 p-6">
        <h3 className="text-xl text-white mb-4">Types of Water and Their Effects</h3>
        
        <div className="space-y-6">
          <div className="border-l-4 border-amber-400 pl-4">
            <h4 className="font-medium">Tap Water</h4>
            <p className="text-sm text-white/80 mt-1">
              Municipal water supply, treated with chlorine or chloramine.
            </p>
            <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <h5 className="text-amber-400 text-sm">Effects on Bread:</h5>
                <ul className="list-disc list-inside text-sm text-white/80 mt-1">
                  <li>Chlorine can inhibit fermentation</li>
                  <li>Mineral content varies by location</li>
                  <li>May affect starter activity</li>
                </ul>
              </div>
              <div>
                <h5 className="text-amber-400 text-sm">Best Practices:</h5>
                <ul className="list-disc list-inside text-sm text-white/80 mt-1">
                  <li>Let stand 24 hours to dissipate chlorine</li>
                  <li>Filter if possible</li>
                  <li>Consider local water hardness</li>
                </ul>
              </div>
            </div>
          </div>
          
          <div className="border-l-4 border-amber-400 pl-4">
            <h4 className="font-medium">Filtered Water</h4>
            <p className="text-sm text-white/80 mt-1">
              Tap water that has been processed to remove impurities, chlorine, and some minerals.
            </p>
            <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <h5 className="text-amber-400 text-sm">Effects on Bread:</h5>
                <ul className="list-disc list-inside text-sm text-white/80 mt-1">
                  <li>Generally produces consistent results</li>
                  <li>Removes chlorine that can inhibit fermentation</li>
                  <li>Maintains beneficial minerals while removing contaminants</li>
                </ul>
              </div>
              <div>
                <h5 className="text-amber-400 text-sm">Best For:</h5>
                <ul className="list-disc list-inside text-sm text-white/80 mt-1">
                  <li>Everyday baking</li>
                  <li>Sourdough starters</li>
                  <li>Consistent results across different recipes</li>
                </ul>
              </div>
            </div>
          </div>
          
          <div className="border-l-4 border-amber-400 pl-4">
            <h4 className="font-medium">Spring Water</h4>
            <p className="text-sm text-white/80 mt-1">
              Natural water with balanced mineral content.
            </p>
            <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <h5 className="text-amber-400 text-sm">Effects on Bread:</h5>
                <ul className="list-disc list-inside text-sm text-white/80 mt-1">
                  <li>Often provides ideal mineral balance for fermentation</li>
                  <li>Free from chlorine and other municipal water treatments</li>
                  <li>Can enhance flavor complexity</li>
                </ul>
              </div>
              <div>
                <h5 className="text-amber-400 text-sm">Best For:</h5>
                <ul className="list-disc list-inside text-sm text-white/80 mt-1">
                  <li>Creating or refreshing sourdough starters</li>
                  <li>Special occasion baking</li>
                  <li>Troubleshooting when tap water might be the issue</li>
                </ul>
              </div>
            </div>
          </div>
          
          <div className="border-l-4 border-amber-400 pl-4">
            <h4 className="font-medium">Distilled Water</h4>
            <p className="text-sm text-white/80 mt-1">
              Water that has been purified to remove virtually all minerals and impurities.
            </p>
            <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <h5 className="text-amber-400 text-sm">Effects on Bread:</h5>
                <ul className="list-disc list-inside text-sm text-white/80 mt-1">
                  <li>Lacks minerals needed for optimal yeast activity</li>
                  <li>Can result in slower fermentation</li>
                  <li>May produce weaker gluten structure</li>
                  <li>May slow down fermentation due to lack of minerals for yeast nutrition</li>
                </ul>
              </div>
              <div>
                <h5 className="text-amber-400 text-sm">Adjustments:</h5>
                <ul className="list-disc list-inside text-sm text-white/80 mt-1">
                  <li>May need slightly lower hydration</li>
                  <li>Consider adding a small amount of mineral-rich salt earlier in the process</li>
                  <li>Might benefit from additional stretch and folds to build strength</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <div className="bg-gray-600/60 p-6">
        <h3 className="text-xl text-white mb-4">Water Temperature Considerations</h3>
        
        <div className="space-y-4">
          <p className="text-white/80">
            Water temperature is a critical factor in controlling dough temperature, which directly affects fermentation rate and bread quality.
          </p>
          
          <div className="bg-amber-500/10 p-4 rounded-lg">
            <h4 className="text-amber-400 font-medium mb-2">Desired Dough Temperature Formula</h4>
            <p className="text-white/80 text-sm">
              Water Temperature = (Desired Dough Temp × 4) - (Room Temp + Flour Temp + Friction Factor)
            </p>
            <p className="text-white/70 text-xs mt-2">
              Friction Factor: ~5°F (3°C) for hand mixing, ~25°F (14°C) for stand mixer
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
            <div className="bg-white/5 p-4 rounded-lg">
              <h4 className="text-lg text-amber-400 mb-2">Cold Water (45-55°F / 7-13°C)</h4>
              <ul className="list-disc list-inside text-sm text-white/80 space-y-1">
                <li>Slows fermentation</li>
                <li>Useful in warm environments</li>
                <li>Gives more control over long fermentations</li>
                <li>Can improve flavor development</li>
              </ul>
            </div>
            
            <div className="bg-white/5 p-4 rounded-lg">
              <h4 className="text-lg text-amber-400 mb-2">Room Temperature (65-75°F / 18-24°C)</h4>
              <ul className="list-disc list-inside text-sm text-white/80 space-y-1">
                <li>Standard for most recipes</li>
                <li>Balanced fermentation rate</li>
                <li>Good for predictable timelines</li>
              </ul>
            </div>
            
            <div className="bg-white/5 p-4 rounded-lg">
              <h4 className="text-lg text-amber-400 mb-2">Warm Water (80-95°F / 27-35°C)</h4>
              <ul className="list-disc list-inside text-sm text-white/80 space-y-1">
                <li>Accelerates fermentation</li>
                <li>Useful in cold environments</li>
                <li>Can help with shorter baking schedules</li>
                <li>May result in less complex flavor</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}